/**
 * Lightweight analytics helper — fires events to our own MongoDB-backed collector AND
 * (optionally) Google Analytics 4 via gtag.
 *
 * GA4 is switched on by an admin (Users & Settings → Analytics) — the measurement ID is
 * fetched from /api/public/site-config at boot (cached in localStorage for instant reuse).
 * REACT_APP_GA4_ID in frontend/.env still works as a build-time fallback.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANON_KEY = "tr_anon_id";
const CONFIG_KEY = "tr_site_config";

let GA4_ID = process.env.REACT_APP_GA4_ID || null;
let gaLoaded = false;
let configLoaded = false;

function getAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = "a_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export const getGA4Id = () => GA4_ID;

/** Point gtag at a (new) measurement ID at runtime — used after the admin saves settings. */
export function setGA4Id(id) {
  const next = id || null;
  if (next === GA4_ID) return;
  GA4_ID = next;
  gaLoaded = false;
  if (GA4_ID) ensureGA();
}

/** Fetch runtime config once per page load; falls back to the cached copy so the first events aren't lost. */
export async function loadSiteConfig() {
  if (configLoaded) return { ga4_id: GA4_ID };
  configLoaded = true;
  try {
    const cached = JSON.parse(localStorage.getItem(CONFIG_KEY) || "null");
    if (cached?.ga4_id) setGA4Id(cached.ga4_id);
  } catch { /* ignore */ }
  try {
    const r = await fetch(`${BACKEND_URL}/api/public/site-config`);
    const cfg = await r.json();
    localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
    setGA4Id(cfg.ga4_id || null);
    return cfg;
  } catch {
    return { ga4_id: GA4_ID };
  }
}

function ensureGA() {
  if (gaLoaded || !GA4_ID || typeof window === "undefined") return;
  gaLoaded = true;
  try {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    s.setAttribute("data-ga4", GA4_ID);
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA4_ID, { send_page_view: false });
  } catch {
    /* no-op */
  }
}

/** SPA page view → GA4 only (our collector tracks product events, not every route change). */
export function trackPageView(path, title) {
  ensureGA();
  try {
    if (GA4_ID && typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "page_view", { page_path: path, page_title: title || document.title, page_location: window.location.href });
    }
  } catch {
    /* no-op */
  }
}

/**
 * trackEvent — fire an analytics event.
 * @param {string} event    - snake_case event name (whitelisted server-side)
 * @param {object} [props]  - JSON-safe payload (plan_key, tier, etc)
 */
export function trackEvent(event, props) {
  if (!event) return;
  ensureGA();
  const payload = {
    event,
    props: props || {},
    anon_id: getAnonId(),
    path: typeof window !== "undefined" ? window.location.pathname : null,
  };
  try {
    if (GA4_ID && typeof window !== "undefined" && window.gtag) {
      window.gtag("event", event, props || {});
    }
  } catch {
    /* no-op */
  }
  try {
    const url = `${BACKEND_URL}/api/public/analytics/track`;
    const body = JSON.stringify(payload);
    if (typeof navigator !== "undefined" && navigator.sendBeacon && typeof Blob !== "undefined") {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch {
    /* no-op */
  }
}
